import { run, unwrap, unwrapOr } from "@maker/result";
import { httpErr } from "@maker/utils/errors";
import { parsePayload, parseResponseProperties } from "@maker/utils/parser";
import type { BodyInit, HeadersInit } from "bun";
import { type FetcherMutationDataAdapter, json } from "./adapter";
import { parseBody, parseBodyMime } from "./parser";
import { buildUrl } from "./url";

type FetcherMutationInputData = BodyInit | FetcherMutationDataAdapter;

type FetcherCommonConfig = Pick<FetcherCommonInput, "headers"> & {
	baseUrl?: string;
};

type FetcherQueryConfig = FetcherCommonConfig;

type FetcherMutationConfig = FetcherCommonConfig & {
	method: "POST" | "PUT" | "DELETE";
};

type FetcherCommonInput = {
	url: string;
	headers?: HeadersInit;
	signal?: RequestInit["signal"];
};

type FetcherQueryInput<S extends FetcherQueryConfig> = (S extends {
	baseUrl: string;
}
	? Omit<FetcherCommonInput, "url"> & Partial<Pick<FetcherCommonInput, "url">>
	: FetcherCommonInput) & {
	data?: Record<string, string | string[]>;
};

type FetcherMutationInput<S extends FetcherMutationConfig> = (S extends {
	baseUrl: string;
}
	? Omit<FetcherCommonInput, "url"> & Partial<Pick<FetcherCommonInput, "url">>
	: FetcherCommonInput) & {
	data?: FetcherMutationInputData;
};

type FetcherCreateConfig = FetcherCommonConfig;

function queryCore<S extends FetcherQueryConfig>(config: S) {
	return (input: FetcherQueryInput<S>) => {
		return run(async () => {
			const url = unwrap(
				buildUrl(`${config.baseUrl ?? ""}${input.url ?? ""}`, input.data),
			);
			const response = await fetch(url, {
				method: "GET",
				headers: {
					...config.headers,
					...input.headers,
				},
				signal: input.signal,
			});

			if (!response.ok) {
				return httpErr.err({
					message: "Fetcher error",
					data: {
						...parseResponseProperties(response),
						payload: unwrapOr(await parsePayload(response), {}),
					},
				});
			}

			return await parsePayload(response);
		});
	};
}

function mutationCore<S extends FetcherMutationConfig>(config: S) {
	return (input: FetcherMutationInput<S>) => {
		return run(async () => {
			const body = unwrap(parseBody(input.data));
			const mime = parseBodyMime(input.data);
			const url = unwrap(buildUrl(`${config.baseUrl ?? ""}${input.url ?? ""}`));
			const response = await fetch(url, {
				method: config.method,
				headers: {
					...config.headers,
					...(mime ? { "content-type": mime } : {}),
					...input.headers,
				},
				signal: input.signal,
				...(body ? { body } : {}),
			});

			if (!response.ok) {
				return httpErr.err({
					message: "Fetcher error",
					data: {
						...parseResponseProperties(response),
						payload: unwrapOr(await parsePayload(response), {}),
					},
				});
			}

			return await parsePayload(response);
		});
	};
}

function createFetcher<C extends FetcherCreateConfig>(config = {} as C) {
	return {
		get: queryCore({ ...config }),
		post: mutationCore({ ...config, method: "POST" }),
		put: mutationCore({ ...config, method: "PUT" }),
		delete: mutationCore({ ...config, method: "DELETE" }),
		adapter: {
			json,
		},
	};
}

const fetcher = {
	...createFetcher(),
	create: createFetcher,
};

export { fetcher, type FetcherMutationInputData };
